import { useEffect, useState } from 'react';
import { accounts } from '../backend';
import { currentWorkspace } from '../workspace';
import { useApp } from '../store';
import { can } from '../permissions';
import { confirmRemoveSamples } from '../sample-note';
import CurrencyRates from './CurrencyRates';
import WorkCalendarEditor from './WorkCalendarEditor';
import Icon from './Icon';

/**
 * THE WORKSPACE'S OWN SETTINGS — how this workspace is named and how its money is
 * reported. The Settings tab of the Workspace hub. What is tenant-wide and an
 * admin's to set lives here, not on a person's Account: a workspace is a thing
 * you configure, not a preference you hold.
 *
 * The currency & rates editor is the shared <CurrencyRates> — the same control,
 * off the same store state, that the board Settings → Finance & payments tab also
 * shows (#488). One source of truth, reachable from both the workspace's own setup
 * and from where the money controls live.
 */
export default function WorkspaceSettings() {
  // The current workspace's name. Read the same way Account did — `workspaces()`
  // runs as its owner, and it never goes through my_tenant(), so it is right even
  // when a token is missing its tenant claim.
  const [name, setName] = useState('');

  // The sample pair's presence, and the right to change it. Managing the samples
  // is a workspace admin's — the same authority that archives boards — so the
  // section shows only to admins (mock 03).
  const role = useApp((s) => s.shownRole);
  const mayManage = can(role, 'manageWorkspace');
  const projects = useApp((s) => s.projects);
  const archivedProjects = useApp((s) => s.archivedProjects);
  const removeSamples = useApp((s) => s.removeSamples);
  const restoreSamples = useApp((s) => s.restoreSamples);
  const samplesPresent = projects.some((p) => p.sample);
  const samplesRemoved = !samplesPresent && archivedProjects.some((p) => p.sample);

  const removeNow = async () => {
    if (await confirmRemoveSamples()) await removeSamples();
  };

  useEffect(() => {
    let stop = false;
    accounts
      .workspaces()
      .then((list) => {
        if (stop) return;
        const here = currentWorkspace() || list[0]?.id || '';
        const mine = list.find((w) => w.id === here) ?? list[0];
        setName(mine?.name ?? '');
      })
      .catch(() => {
        setName('');
      });
    return () => {
      stop = true;
    };
  }, []);

  return (
    <div className="settings">
      <section className="panel">
        <h2>General</h2>
        <div className="stack">
          <span className="stack-lbl">Workspace name</span>
          <p className="ws-name-read">{name || '—'}</p>
        </div>
        {/* The name (and the address, and whether the workspace is suspended) is
            the platform operator's to change, not a workspace admin's — the same
            boundary the database draws (0117). Shown here so the workspace can be
            identified at a glance; renaming is done by the operator. */}
        <p className="hint">
          The name people see in the switcher and on the emails this workspace sends. It is set by the
          platform operator.
        </p>
      </section>

      <section className="panel">
        <h2>Currency &amp; Rates</h2>
        <CurrencyRates />
      </section>

      <section className="panel">
        <h2>Working Days &amp; Holidays</h2>
        <p className="hint">
          The days your organisation works and the holidays it closes for. Forecasts and reminders
          count only working days, so a known close pushes a prediction out.
        </p>
        <WorkCalendarEditor />
      </section>

      {/* SAMPLE BOARDS — the authoritative home for removing the read-only
          Example Co. pair, and the ONLY place they are brought back, so the
          board picker never has to carry a "bring back" row (mock 03). One row,
          two states: present → a quiet "Remove them"; removed → the petrol
          "Bring them back". Shown only to a workspace admin, and only when the
          pair exists to manage at all. */}
      {mayManage && (samplesPresent || samplesRemoved) && (
        <section className="panel">
          <h2>Sample Boards</h2>
          <p>
            The Example Co. sample boards — a finished delivery project and a service board — help new
            members see a well-run board. They're read-only and never appear in your reports or totals.
          </p>
          {samplesPresent ? (
            <div className="sample-set-row">
              <span className="sample-set-ic present">
                <Icon name="eye" size={18} />
              </span>
              <span className="sample-set-id">
                <span className="sample-set-name">In your board list</span>
                <span className="sample-set-sub">
                  Shown under “Sample · read only” in the board picker, for everyone in this workspace.
                </span>
              </span>
              <button className="btn" onClick={() => void removeNow()}>
                Remove them
              </button>
            </div>
          ) : (
            <div className="sample-set-row">
              <span className="sample-set-ic">
                <Icon name="trash" size={18} />
              </span>
              <span className="sample-set-id">
                <span className="sample-set-name">Removed</span>
                <span className="sample-set-sub">
                  Taken out of the board list. Bring them back whenever you like.
                </span>
              </span>
              {/* Only adds, never loses — so no confirm. A brief toast confirms it. */}
              <button className="btn btn-primary" onClick={() => void restoreSamples()}>
                Bring them back
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
